import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { debounce } from "es-toolkit";
import { selectOrderByUuid } from "@/modules/pg/shared/planningTable/reducers/planningTable/selectors";
import EditableTextArea from "@/modules/shared/ui/EditableTextArea";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as Auth from "@/lib/auth";
import { useResolvedOrderComment } from "@/lib/custom-hooks/queryComment";
import { onSaveComment } from "@/modules/pg/shared/planningTable/modals/PlanningTableEditModal";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { IOrderProducedModelEx } from "@/modules/pg/shared/planningTable/reducers/linesOrders";

const SAVE_DEBOUNCE_TIME_MS = 1000;

type Props = {
    order_uuid: string;
    comment: string;
};

const OrderInlineComment = (props: Props) => {
    const { order_uuid, comment } = props;

    const { resolvedComment, updateOrderComment } = useResolvedOrderComment(order_uuid, comment);

    const [inputComment, setInputComment] = useState(resolvedComment);
    const inputCommentRef = useRef(inputComment);
    inputCommentRef.current = inputComment;

    const order = useSelector((state: ReduxState) => selectOrderByUuid(state, order_uuid));
    const store = useStore<ReduxState>();
    const storeRef = useRef(store);
    storeRef.current = store;

    /** A single debounced function instance that is stable and not recreated on every render */
    const debouncedSave = useMemo(
        () =>
            debounce((order: IOrderProducedModelEx, newComment: string) => {
                updateOrderComment.mutate(
                    { order, comment: newComment },
                    {
                        onSuccess: () => {
                            if (newComment !== inputCommentRef.current) {
                                return;
                            }
                            onSaveComment(order.uuid, newComment, storeRef.current);
                        },
                    }
                );
            }, SAVE_DEBOUNCE_TIME_MS),
        // Dependencies are not required here — maintain a single instance of the function
        // mutate is stable; inputCommentRef and storeRef are read at success time.
        []
    );

    useEffect(() => {
        setInputComment(resolvedComment);
        inputCommentRef.current = resolvedComment;
    }, [resolvedComment]);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [order_uuid, debouncedSave]);

    const handleChange = useCallback(
        (value: string) => {
            setInputComment(value);
            inputCommentRef.current = value;
            if (!order) {
                return;
            }
            if (value !== resolvedComment) {
                debouncedSave(order, value);
            } else {
                debouncedSave.cancel();
            }
        },
        [order, resolvedComment, debouncedSave]
    );

    const isDisabled = !Auth.hasPermission(Auth.PERMISSION_NAMES.OrderEdit);

    return (
        <EditableTextArea
            value={inputComment}
            onChange={handleChange}
            isDisabled={isDisabled}
            placeholder={translate("common.add_comment", "Add comment")}
            title={translate("common.comment", "Comment")}
            size="small"
        />
    );
};

export default OrderInlineComment;
